import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ClockIcon,
  PlayIcon,
  StopIcon,
  XCircleIcon,
  PencilIcon,
  UserGroupIcon,
  WrenchScrewdriverIcon,
  MapPinIcon,
  CheckIcon,
  ArrowDownTrayIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { Button, IconButton } from '../ui/Button';
import DropdownMenu from '../ui/DropdownMenu';
import { Card, CardHeader, CardBody } from '../ui/Card';
import { TimePicker, DatePicker, Input, SelectMenu, NumberField } from '../ui';
import DetailPageHeader from '../ui/DetailPageHeader';
import { useConfirm } from '../ui/ConfirmationModal';
import { Dialog, DialogHeader, DialogBody } from '../ui/Dialog';
import { useSettingsContext } from '../../hooks/useSettings';
import { scheduleForEdit, woTypeValue, woTypeLabel, changedScalarFields, WO_TYPE_REQUIRED_MSG, woDisplayNumber } from '../../utils/workOrderEdit';
import { scheduleWrite, scheduleError, woCompletionLabel, woOpenClockWarning } from '../../utils/woSchedule';
import { confirmAndDeleteWO } from '../../utils/woDelete';
import useTechOptions from '../../hooks/useTechOptions';
import { deactivatedChipOptions } from '../../utils/userStatus';
import useSettingsList from '../../hooks/useSettingsList';
import TypeaheadChips from '../ui/TypeaheadChips';
import { normalizeWoStatus } from '../../utils/normalizeWoStatus';
import { humanizeEnum } from '../../utils/humanize';
import { crewIdentity, crewPickerOptions } from '../../utils/crewIdentity';
import { materialQuoted, materialUsed, fmtQty, noMaterialsMessage } from '../../utils/woMaterials';
import { correctTimeEntry, toHHMM, entryDurationMs, parseStamp } from '../../utils/timeEntryEdit';
import { Alert } from '../ui/Alert';
import { SkeletonCard } from '../ui/Skeleton';
import { NotesPanel } from '../ui/NotesPanel';
import { woNotes } from '../../utils/woNotes';
import { editListsForSave, changedEditLists } from '../../utils/woEditDiff';
import GoldenCustomerCard from '../ui/GoldenCustomerCard';
import WorkOrderHeaderStrip from './WorkOrderHeaderStrip';
import { getJobSiteAddress, resolveWorkOrderJobSiteString } from '../../utils/customerAddress';
import api, { unwrapList } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { isFieldTech as isFieldTechUser } from '../../utils/isFieldTech';
import { AttachModal } from '../attachments/AttachModal';
import { isEnabled } from '../../config/flags';
import { entryDisplay } from '../../utils/attachmentEntry';
import FilePreviewModal from '../attachments/FilePreviewModal';
// ─── WO State Machine ────────────────────────────────────────────────────────
// States: Active · Completed (§2 — no "In Progress" anywhere in BIDIT)
// Clock In/Out track field-tech time; they do NOT change WO status.
// Transitions:
//   Active → Completed (derived at stop, IDI-466)
//   Any state → deleted (IDI-469; nothing writes 'cancelled' any more)


// ─── Time Entry Component ────────────────────────────────────────────────────

// PT-28 cold-eye 3 (S1 crash class): shared array-safe helper.
//
// `(x || []).map(...)` is NOT crash-proof — a TRUTHY non-array (string, number,
// object) falls through the `||` and crashes `.map` at runtime. This is what
// let `wo-1784923541566-keuimc` white-screen after cold-eye 2's partial sweep.
// `asArray` is Array.isArray-strict: only real arrays pass through, everything
// else (including truthy non-arrays) becomes `[]`. Every `.map` site in this
// file goes through asArray so the crash class cannot survive a future backend
// shape drift or a partial sweep.
const asArray = (v) => (Array.isArray(v) ? v : []);

function TimeEntry({ entry, onCorrect, canEdit = true, correctedBy }) {
  const [editing, setEditing] = useState(false);
  const [correctedIn, setCorrectedIn] = useState('');
  const [correctedOut, setCorrectedOut] = useState('');
  const [correctionError, setCorrectionError] = useState('');

  // IDI-473: rebuild FULL timestamps from the entry's date + the edited HH:mm.
  // Saving the bare "16:30" made the entry Invalid and dropped it from totals.
  const handleSaveCorrection = () => {
    const res = correctTimeEntry(entry, { clockIn: correctedIn, clockOut: correctedOut }, { by: correctedBy });
    if (!res.ok) {
      setCorrectionError(res.error);
      return;
    }
    setCorrectionError('');
    onCorrect(entry.id, res.entry);
    setEditing(false);
  };

  const formatDuration = (startTime, endTime) => {
    const start = parseStamp(startTime);
    const end = parseStamp(endTime);
    if (!start || !end) return '—';
    const diffMs = end - start;
    // Guard against negative spans.
    if (diffMs < 0) return '—';
    const hours = Math.floor(diffMs / 3600000);
    const minutes = Math.floor((diffMs % 3600000) / 60000);
    return `${hours}h ${minutes}m`;
  };

  const formatTimeShort = (dateStr) => {
    if (!dateStr) return '—';
    // PT-21 (21.6.1): guard against Invalid Date so time-tracking rows never
    // render the string "Invalid Date" — mirrors formatDuration's NaN guard.
    const d = parseStamp(dateStr);
    if (!d) return '—';
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="py-2 border-b border-gray-100 last:border-0">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ClockIcon className="h-4 w-4 text-gray-400" />
          <div>
            <p className="text-sm text-gray-900">
              {entry.memberName || 'Team Member'}
            </p>
            <p className="text-xs text-gray-500">
              {formatTimeShort(entry.clockIn)}
              {' → '}
              {entry.clockOut ? formatTimeShort(entry.clockOut) : 'Active'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">
            {formatDuration(entry.clockIn, entry.clockOut)}
          </span>
          {entry.corrected && (
            <PencilIcon className="h-3 w-3 text-amber-600 inline" title="Time was corrected" aria-label="Time was corrected" />
          )}
          {canEdit && (
            <button
              onClick={() => {
                setCorrectedIn(toHHMM(entry.clockIn));
                setCorrectedOut(toHHMM(entry.clockOut));
                setCorrectionError('');
                setEditing(!editing);
              }}
              className="text-gray-400 hover:text-gray-600"
              title="Correct time"
            >
              <PencilIcon className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
      {editing && (
        <div className="mt-2 ml-7 flex items-center gap-2 bg-amber-50 rounded p-2">
          <TimePicker
            typed
            aria-label="Clock-in"
            value={correctedIn}
            onChange={(v) => setCorrectedIn(v)}
            fieldClassName="text-xs"
          />
          <span className="text-xs text-gray-400">→</span>
          <TimePicker
            typed
            aria-label="Clock-out"
            value={correctedOut}
            onChange={(v) => setCorrectedOut(v)}
            fieldClassName="text-xs"
          />
          <button
            onClick={handleSaveCorrection}
            className="text-xs font-medium text-red-600 hover:text-red-700 px-2"
          >
            Save
          </button>
          <button
            onClick={() => setEditing(false)}
            className="text-xs text-gray-500 hover:text-gray-700"
          >
            Cancel
          </button>
        </div>
      )}
      {editing && correctionError && (
        <p className="mt-1 ml-7 text-xs" role="alert" style={{ color: 'var(--color-error)' }}>{correctionError}</p>
      )}
    </div>
  );
}

// ─── Materials Section ───────────────────────────────────────────────────────

function MaterialsSection({ materials, onUpdateMaterial, wo, rowErrors = {} }) {
  // PT-28-remediation: array-safe prop guard. Even though callers pass
  // `wo.materials || []`, don't trust the invariant — crash-class hardening.
  const safeMaterials = Array.isArray(materials) ? materials : [];
  if (safeMaterials.length === 0) {
    return (
      <div className="text-center py-6">
        <WrenchScrewdriverIcon className="h-6 w-6 text-gray-300 mx-auto mb-2" />
        <p className="text-xs text-gray-500">{noMaterialsMessage(wo)}</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {safeMaterials.map((mat, idx) => {
        // IDI-816: Enter and "add" share one submit; the box clears only
        // once the save succeeds, so a refusal keeps the typed amount.
        const submit = async (input) => {
          if (!input || !input.value) return;
          const val = parseInt(input.value, 10);
          if (!Number.isFinite(val) || val === 0) {
            // QA: say why nothing happened instead of a silent no-op.
            input.setCustomValidity('Enter a whole number other than 0');
            input.reportValidity();
            return;
          }
          input.setCustomValidity('');
          const ok = await onUpdateMaterial(idx, val);
          if (ok) input.value = '';
        };
        const rowError = rowErrors[idx];
        return (
        <div key={mat.id || idx} className="py-2 border-b border-gray-100 last:border-0">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-900">{mat.name}</p>
            <p className="text-xs text-gray-500">{mat.unit || 'ea'}</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              {/* IDI-315 §5 (2026-09-12): flip to Quoted-first — was
                  `{used} / {total}` → `used / total`. Header at :1613 was
                  flipped in the same PR ("Materials (Used / Total)" →
                  "Materials (Quoted / Used)"); this per-row rendering had
                  the identical reversed order and would have disagreed with
                  the corrected header if left alone — confirmed by reading
                  both sites before fixing, per the review's own warning to
                  check whether both surfaces carry the wrong order. */}
              {/* IDI-187: one reader for quoted/used (the row's quantity was
                  stored as `quantity`/`coverage`; reading `total` showed 0). */}
              {(() => {
                const quoted = materialQuoted(mat);
                const used = materialUsed(mat);
                return (
                  <>
                    <p className="text-sm font-medium text-gray-900">
                      {fmtQty(quoted)} / {fmtQty(used)}
                    </p>
                    <p className="text-xs text-gray-500">
                      quoted / used
                      {quoted == null || quoted === 0 ? null
                        : used < quoted ? <span className="ml-1 text-gray-400">({fmtQty(quoted - used)} remaining)</span>
                          : used === quoted ? <span className="ml-1 text-green-600 inline-flex items-center gap-1"><CheckIcon className="h-3 w-3" />all used</span>
                            : <span className="ml-1 text-amber-600">+{fmtQty(used - quoted)} over</span>}
                    </p>
                  </>
                );
              })()}
            </div>
            <div className="flex items-center gap-1">
              <input
                type="number"
                min="0"
                max={materialQuoted(mat) != null ? Math.max(0, materialQuoted(mat) - materialUsed(mat)) : undefined}
                placeholder="+"
                className="w-12 rounded border px-1.5 py-1 text-xs text-center"
                style={{ borderColor: 'var(--color-border)' }}
                onInput={(e) => e.target.setCustomValidity('')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') submit(e.target);
                }}
              />
              <button
                type="button"
                className="text-[10px] text-gray-500 hover:text-gray-700 underline"
                onClick={(e) => submit(e.currentTarget.parentElement.querySelector('input'))}
              >
                add
              </button>
            </div>
          </div>
        </div>
        {rowError && (
          <p className="mt-1 text-right text-xs text-red-600" role="alert">{rowError}</p>
        )}
        </div>
        );
      })}
    </div>
  );
}

// ─── Main WO Detail Component ────────────────────────────────────────────────

// PT-22 W3-fp1 F2: scalar version of normalizeAssignmentsForEdit for crew.
// Same self-heal contract — legacy `wo.crew='Alpha Dogs'` with no `wo.crewId`
// looks up Settings › Crews and adopts the real ID on hit. Applied on BOTH
// edit-entry AND display so a legacy record renders the same shape across
// screens (no more "dashed in display, resolved in edit" split-brain).
//
// Returns { crew, crewId, resolved } where `resolved` is true iff either an
// explicit `rawId` was provided or a case/whitespace-insensitive name-match
// hit against the pool.
// IDI-472: delegates to utils/crewIdentity over the RAW Settings crew list
// (ids, names, live colours). The old version trusted any rawId and keyed the
// pool by getOptions' values, which are name slugs for UI-made crews, so a
// crew showed as its id ("test_crew") and was saved under it.
function resolveCrew(rawName, rawId, crews) {
  const ident = crewIdentity({ crew: rawName, crewName: rawName, crewId: rawId }, crews);
  return { crew: ident.name, crewId: ident.resolved ? ident.crewId : '', resolved: ident.resolved, color: ident.color };
}

// PT-22 W3 §3.5: normalize a raw techs/resources array (mixed shapes: string
// name, {name}, {id, name}) into a uniform edit-mode shape that carries a
// `resolved` flag. Name-only entries are looked up against the current
// directory pool; matches self-heal by picking up the directory ID. Chip
// rendering + save-side dual-write both read `resolved`.
function normalizeAssignmentsForEdit(raw, pool) {
  const list = Array.isArray(pool) ? pool : [];
  const findByName = (name) =>
    list.find(
      (o) => (o.label || '').toLowerCase() === (name || '').trim().toLowerCase()
    );
  return (Array.isArray(raw) ? raw : [])
    .map((t) => {
      if (typeof t === 'string') {
        const hit = findByName(t);
        return hit
          ? { id: hit.value, name: hit.label, resolved: true }
          : { id: null, name: t, resolved: false };
      }
      if (t && typeof t === 'object') {
        if (t.id) return { id: t.id, name: t.name || t.id, resolved: true, category: t.category };
        if (t.name) {
          const hit = findByName(t.name);
          return hit
            ? { id: hit.value, name: hit.label, resolved: true }
            : { id: null, name: t.name, resolved: false, category: t.category };
        }
      }
      return null;
    })
    .filter(Boolean);
}

// IDI-553: newest first by edit-or-create time (an edited note moves to the
// top); equal / unknown times keep stored order.
function noteTime(n) {
  const t = Date.parse(n?.updated_at || n?.created_at || '');
  return Number.isNaN(t) ? 0 : t;
}
function sortWoNotesNewestFirst(list) {
  return list
    .map((n, i) => ({ n, i }))
    .sort((a, b) => (noteTime(b.n) - noteTime(a.n)) || (a.i - b.i))
    .map(({ n }) => n);
}

export default function WorkOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  // IDI-598 D3: the shared field-tech rule (groups first, else role).
  const isFieldTech = isFieldTechUser(user);
  const confirm = useConfirm();
  const settingsCtx = useSettingsContext();
  const { getEnumLabel, getOptions } = settingsCtx;
  // PT-22 W3 §3.4: tech + resource directory pools drive both the edit-mode
  // type-ahead options and the resolve-on-edit self-heal (§3.5). Only enabled
  // when the operator has edit rights (isFieldTech has no edit access).
  const { options: techPool, deactivatedIds: deactivatedTechIds } = useTechOptions({ enabled: !isFieldTech });
  const { items: resourceInstances } = useSettingsList('resource_types');
  // IDI-472: the raw crew list (id, name, live colour). useSettingsList
  // refreshes when Settings writes crews, so a new crew needs no reload.
  const { items: crewList } = useSettingsList('crews');
  const resourcePool = useMemo(
    () =>
      (Array.isArray(resourceInstances) ? resourceInstances : []).map((r) => {
        // FP2-F6: title-case seed categories via getEnumLabel; tenant custom values
        // pass through unchanged. Fixes '202 Mid-Range · equipment' → '· Equipment'.
        const catLabel = r.category
          ? (getEnumLabel?.('resource_category', r.category) || r.category)
          : '';
        return {
          value: r.id,
          label: catLabel ? `${r.name} · ${catLabel}` : r.name,
          // Preserve raw category so save-side + display can round-trip it.
          category: r.category,
        };
      }),
    [resourceInstances, getEnumLabel]
  );
  const [wo, setWO] = useState(null);
  // PT-53b commit 1: AttachModal (see components/attachments/AttachModal.js).
  // Entry-point CTA is flag-gated (FLAGS.ATTACHMENTS_UPLOAD) until
  // S3_BUCKET_FILES lands on the deployed Lambda (CTO note pending).
  const [attachOpen, setAttachOpen] = useState(false);
  // PT-28 (28.6): Fetched customer for the top-of-page GoldenCustomerCard so
  // the WO surface renders the full WO-2 §149 field set (primary contact w/
  // role, service/billing address, terms, sameAsService flag). Sourced via
  // the parent job's customer_id in fetchWorkOrder below.
  const [customer, setCustomer] = useState(null);
  // PT-21 (W3): Inline edit mode (WO-2 State B). editDraft stages every edited
  // section; handleSave() flushes via persistWO, handleCancel() discards.
  const [editMode, setEditMode] = useState(false);
  const [crewModalOpen, setCrewModalOpen] = useState(false);
  const [editDraft, setEditDraft] = useState(null);
  // IDI-440: the scalar values Edit opened with (diffed on Save).
  const editSeedRef = useRef({});
  const [savingEdit, setSavingEdit] = useState(false);
  // IDI-563: files removed in Edit are held here (attachmentIds) until Save;
  // Cancel drops the set, so the file comes back.
  const [pendingFileRemovals, setPendingFileRemovals] = useState([]);
  // IDI-471: "Stop must be after start."
  const [editScheduleError, setEditScheduleError] = useState('');
  // IDI-517: "Choose a work-order type" (checked on Save).
  const [editTypeError, setEditTypeError] = useState('');
  // PT-22 W3 §3.4-EDIT: Techs + Resources edit surfaces converged onto
  // TypeaheadChips; the legacy `newTechName` / `newResourceName` state and
  // their Input+Add blocks are gone. `appendDraftListItem` /
  // `removeDraftListItem` still serve Materials / Attachments (Line Items UI
  // was retired by IDI-265; the writers at :550/:603 remain intact for the
  // WO↔line-item join).
  // Work orders live embedded in the parent job (job.work_orders[]) — there is
  // no standalone work-orders table. We load + persist through the job so the
  // clock-in/out chain (timeEntries → actualHours) survives a refresh and
  // shows up in Job Costing. (REQ-WO-01 / S1.)
  // IDI-475: the job id also rides in the URL (?job=), so a refresh or a new
  // tab opens THIS job's work order instead of the first id match in a scan.
  const urlJobId = new URLSearchParams(location.search).get('job') || null;
  const [jobId, setJobId] = useState(location.state?.jobId || urlJobId);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // IDI-445: the attachment open in the preview modal (view mode).
  const [previewAtt, setPreviewAtt] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');

  useEffect(() => {
    fetchWorkOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Locate this work order inside a job's embedded work_orders array.
  const findInJob = (job) => {
    const list = job?.work_orders || job?.workOrders || [];
    return list.find((w) => (w.id || w.workOrderId) === id) || null;
  };

  // PT-28 (28.6): Fetch customer via parent job's customer_id so the top-of-page
  // GoldenCustomerCard can render the full WO-2 §149 field set. Non-critical:
  // failure falls back to the WO's embedded customer* fields.
  //
  // PT-28-remediation (400-loop root cause): the api client did not validate
  // ids before building `/customers/${id}` — a `custId` that stringifies to
  // 'undefined' / 'null' / '' would hit the backend and 400. Under React
  // StrictMode's double-mount this looks like a spam / loop in devtools. Guard
  // aggressively: require a non-empty string that isn't literally the strings
  // 'undefined' or 'null'.
  const hydrateCustomerFromJob = async (job) => {
    const raw = job?.customer_id ?? job?.customerId;
    const custId = raw == null ? '' : String(raw).trim();
    if (!custId || custId === 'undefined' || custId === 'null') return;
    try {
      const custData = await api.getCustomer(custId);
      setCustomer(custData);
    } catch (_err) {
      // Non-critical — card will fall back to job-level fields.
    }
  };

  const fetchWorkOrder = async () => {
    try {
      setLoading(true);
      setError(null);

      // Primary path: load the parent job and read the embedded work order.
      // PT-28-remediation: wrap the primary getJob in an inner try/catch so a
      // stale/invalid `knownJobId` (400) does NOT abort the outer path —
      // instead we silently fall through to the getJobs() scan below. Before
      // this fix, a router-state jobId pointing at a deleted/inaccessible job
      // would surface as "Failed to load work order" even though the WO could
      // be found by scanning jobs.
      const knownJobId = location.state?.jobId || urlJobId || jobId;
      if (knownJobId) {
        try {
          const job = await api.getJob(knownJobId);
          const found = findInJob(job);
          if (found) {
            setJobId(knownJobId);
            setWO({ ...found, jobId: knownJobId, parentJob: job });
            setNotes(woNotes(found));
            await hydrateCustomerFromJob(job);
            return;
          }
        } catch (_primaryErr) {
          // Fall through to the getJobs() scan below — the stale jobId is
          // recoverable and shouldn't kill discovery.
        }
      }

      // Fallback: scan jobs for the one containing this work order
      // (covers deep-links / refreshes that arrive without router state).
      // IDI-141: wrapped `{jobs, has_more}` envelope from the capped endpoint —
      // prior triple-fallback read `.data || .items` (never `.jobs`) and would
      // silently short-circuit the fallback scan, making deep-link/refresh
      // resolutions look like "not found" for any WO not on the router state.
      const jobsRes = await api.getJobs();
      const { list } = unwrapList(jobsRes, 'jobs', 'WorkOrderDetail');
      for (const j of list) {
        const found = findInJob(j);
        if (found) {
          setJobId(j.id);
          setWO({ ...found, jobId: j.id, parentJob: j });
          setNotes(woNotes(found));
          await hydrateCustomerFromJob(j);
          return;
        }
      }
      setWO(null); // genuinely not found → shows "not found" UI
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        setError('You are not authorized to view this work order');
      } else {
        setError('Failed to load work order');
      }
    } finally {
      setLoading(false);
    }
  };

  // Persist work-order changes back into the parent job's work_orders array.
  const persistWO = async (updates) => {
    const jid = jobId || wo?.jobId;
    if (!jid) throw new Error('No parent job for this work order');
    // IDI-453: send only this WO's changes; the server merges them into the
    // fresh job record, so a teammate's edit to another WO on this job is
    // never overwritten. A 409 means the job kept changing under us.
    return api.updateWorkOrder(jid, id, updates);
  };

  // PT-53b commit 2 (2026-08-03): the client-side ref-write to wo.attachments[]
  // was moved SERVER-SIDE into `createAttachment` (backend-go/cmd/quotes/attachments.go).
  // For work_order owner_type the handler fetches the parent job, locates the
  // WO in job.work_orders[], appends the ref to that WO's attachments[], and
  // TransactWriteItems the Attachment record + the updated job atomically.
  //
  // What this handler still does: mirror the ref into local state for an
  // instant UI update. The server is now the durable truth AND the atomic
  // writer; if createAttachment's transaction failed, AttachModal surfaces
  // the error and this handler is never called.
  const handleAttached = (ref) => {
    // ref shape: { attachmentId, source, filename, contentType, sizeBytes, attachedAt }
    setWO((prev) => ({ ...prev, attachments: [...asArray(prev?.attachments), ref] }));
  };

  // §2: WO statuses are Active · Completed only — no "In Progress".
  // Clock In/Out are time-tracking actions; they do NOT change WO status.
  const isClockedIn = !!(wo?.clockedInAt || wo?.clocked_in_at);

  // IDI-467 (D-01): there is no manual Complete. A WO completes when its
  // stop passes (derived server-side, IDI-466). Completed is a status, not a
  // lock (B-13): Edit, Delete and the clock stay available.
  const completionLabel = woCompletionLabel(wo);
  const openClockWarning = woOpenClockWarning(wo);

  // IDI-469: Delete replaces Cancel. Any state; paid roles only (B-03).
  // IDI-598 D3: Delete needs Edit (not a field tech) and a role.
  const canDelete = !isFieldTech && !!user?.role;
  const handleDeleteWO = async () => {
    const jid = jobId || wo?.jobId || wo?.job_id;
    try {
      const gone = await confirmAndDeleteWO({ confirm, wo: { ...wo, id }, jobId: jid });
      if (gone) navigate(`/jobs/${jid}?tab=work-orders`);
    } catch (err) {
      setError(err?.message || 'Failed to delete work order');
    }
  };

  // PT-21 (W3, 21.2.1/2): edit-mode helpers.
  const enterEditMode = () => {
    if (isFieldTech) return;
    // IDI-440: read the schedule + type through the SAME aliases the view uses
    // (a migrated WO stores a full ISO start and a typeId), and remember what
    // the form opened with so Save writes only what the user changed.
    const seededScalars = {
      ...scheduleForEdit(wo),
      woType: woTypeValue(wo),
    };
    editSeedRef.current = seededScalars;
    setEditTypeError('');
    const draft = {
      ...seededScalars,
      // PT-22 W3-fp1 F2: crew gets the same self-heal as techs/resources.
      // Legacy `wo.crew='Alpha Dogs'` with no `wo.crewId` — look up Settings
      // › Crews; on hit adopt the real ID; on miss keep the raw name as
      // an unresolved chip that saves back as { crewId:null, crew:name }.
      ...(() => {
        const c = resolveCrew(wo.crew, wo.crewId, crewOptions);
        editSeedRef.current.crewId = c.crewId;
        return { crew: c.crew, crewId: c.crewId };
      })(),
      // PT-22 W3 §3.5: normalize + resolve names against current directory pools
      // (self-heal name-only legacy records on next edit-save).
      techs: normalizeAssignmentsForEdit(
        Array.isArray(wo.techs) ? wo.techs : (Array.isArray(wo.assignedTechs) ? wo.assignedTechs : []),
        techPool
      ),
      lineItems: Array.isArray(wo.lineItems) ? wo.lineItems.map((it) => ({ ...it })) : [],
      materials: Array.isArray(wo.materials) ? wo.materials.map((m) => ({ ...m })) : [],
      resources: normalizeAssignmentsForEdit(
        Array.isArray(wo.resources) ? wo.resources : [],
        resourcePool
      ),
      // IDI-453: attachments are NOT part of the draft. A file attached in
      // Edit is written server-side at once (createAttachment's companion
      // write) and a removal is its own delete, so Save never sends them.
    };
    // IDI-453 (10-13): remember the lists the form opened with, in their
    // save shape, so Save sends a list only when the user changed it.
    editSeedRef.current.lists = editListsForSave(draft);
    setEditDraft(draft);
    setEditMode(true);
  };

  const handleCancelEdit = () => {
    setEditTypeError('');
    setPendingFileRemovals([]);
    setEditDraft(null);
    setEditMode(false);
  };

  const handleSaveEdit = async () => {
    if (!editDraft) return;
    try {
      setSavingEdit(true);
      // PT-22 W3 §3.5: dual-write techs + resources. IDs authoritative in
      // techIds/resourceIds; resolved-only. Names round-trip via the
      // techs/resources snapshot alongside; unresolved (name-only) entries
      // survive with id:null and self-heal on the next edit-save when a
      // directory match appears.
      // IDI-453 (10-13): each list is sent only when it differs from what the
      // form opened with, independently of crew, so a tech added beside a
      // crew saves (F-27) and a date-only Edit sends no lists at all (B4.3).
      const changedLists = changedEditLists(editSeedRef.current && editSeedRef.current.lists, editDraft);

      // IDI-440: schedule + type are written only when changed, so saving
      // an untouched migrated WO never blanks or rewrites them. Crew likewise.
      const seed = editSeedRef.current || {};
      const crewChanged = String(editDraft.crewId || '') !== String(seed.crewId || '');
      // IDI-471 + U-13: a changed schedule is written whole -- the local pairs
      // plus startAt/stopAt as absolute instants -- so every reader (and the
      // server's auto-complete, IDI-466) agrees on when it stops.
      const scheduleKeys = ['scheduledDate', 'startTime', 'endDate', 'endTime'];
      const scheduleChanged = Object.keys(changedScalarFields(seed, editDraft, scheduleKeys)).length > 0;
      // IDI-467 (D-02): a scheduled WO can't be saved without a stop.
      const stopMissing = scheduleChanged && editDraft.scheduledDate && !(editDraft.endDate && editDraft.endTime);
      const schedErr = stopMissing
        ? 'A scheduled work order needs a stop date and time.'
        : (scheduleChanged ? scheduleError({
          startDate: editDraft.scheduledDate, startTime: editDraft.startTime,
          stopDate: editDraft.endDate, stopTime: editDraft.endTime,
        }) : '');
      // IDI-517: a work order needs a type. A typeless (migrated or older)
      // WO can't be saved from Edit until one is picked.
      // `?? ''` (not `||`) so a legacy numeric type id of 0 still counts as a type.
      const typeMissing = !String(editDraft.woType ?? '').trim();
      setEditTypeError(typeMissing ? WO_TYPE_REQUIRED_MSG : '');
      setEditScheduleError(schedErr || '');
      if (typeMissing || schedErr) {
        setSavingEdit(false);
        return;
      }
      const updates = {
        ...changedScalarFields(seed, editDraft, ['woType']),
        ...(scheduleChanged ? scheduleWrite({
          startDate: editDraft.scheduledDate, startTime: editDraft.startTime,
          stopDate: editDraft.endDate, stopTime: editDraft.endTime,
        }) : {}),
        ...(crewChanged ? { crew: editDraft.crew || null, crewId: editDraft.crewId || null } : {}),
        ...changedLists,
      };
      if (Object.keys(updates).length === 0) {
        await flushPendingFileRemovals();
        setEditDraft(null);
        setEditMode(false);
        return;
      }
      // The saved job is the truth: the server keeps each surviving row's
      // check-out, Qty Used and hidden flag (Adrian 10-20).
      const saved = await persistWO(updates);
      const found = saved ? findInJob(saved) : null;
      if (found) {
        setWO((prev) => ({ ...found, jobId: prev?.jobId, parentJob: saved }));
        setNotes(woNotes(found));
      } else {
        setWO((prev) => ({ ...prev, ...updates }));
      }
      // IDI-563: files removed in Edit are deleted only once the save landed.
      await flushPendingFileRemovals();
      setEditDraft(null);
      setEditMode(false);
    } catch (err) {
      setError(`Failed to save changes: ${err.message}`);
    } finally {
      setSavingEdit(false);
    }
  };

  // IDI-453 (same work order): every item write below is ONE server action on
  // the fresh job record (rev-guarded). The server stamps ids, authors and
  // actors and returns the saved job, which replaces local state, so a
  // teammate's note / clock / check-out made since this page loaded shows up
  // instead of being overwritten.
  // IDI-563: the saved job comes back through the same read enrichment a GET
  // uses (derived status, running clock, labels, migrated crew and techs,
  // dates), so adopting it as-is shows what a read shows -- no client-side
  // re-derivation, and a following Edit seeds from the migrated techs.
  const applySavedJob = (job) => {
    const found = job ? findInJob(job) : null;
    if (!found) {
      fetchWorkOrder();
      return;
    }
    const jid = jobId || wo?.jobId;
    setWO((prev) => ({ ...found, jobId: jid || prev?.jobId, parentJob: job }));
    setNotes(woNotes(found));
  };
  const [materialErrors, setMaterialErrors] = useState({});
  const materialSaving = useRef(new Set());
  const runItemAction = async (call, failMsg) => {
    const jid = jobId || wo?.jobId;
    if (!jid) throw new Error('No parent job for this work order');
    try {
      applySavedJob(await call(jid));
    } catch (err) {
      setError(err?.friendlyMessage || (failMsg ? `${failMsg}: ${err.message}` : err.message));
      throw err;
    }
  };

  const handleClockIn = async () => {
    setActionLoading(true);
    try {
      await runItemAction((jid) => api.addTimeEntry(jid, id), 'Failed to clock in');
    } catch (_) { /* surfaced */ } finally {
      setActionLoading(false);
    }
  };

  const handleClockOut = async () => {
    setActionLoading(true);
    try {
      // The server closes the caller's newest open entry (or a legacy
      // member-less clock) and recomputes actualHours + clockedInAt.
      await runItemAction((jid) => api.closeTimeEntry(jid, id), 'Failed to clock out');
    } catch (_) { /* surfaced */ } finally {
      setActionLoading(false);
    }
  };

  // REQ-WIRE-09 / IDI-453: resource check-out / check-in names the row; the
  // server stamps who and when ("Checked out by <name>, <time>", L-08).
  const handleResourceCheck = async (idx, field) => {
    const raw = asArray(wo.resources)[idx];
    const ref = typeof raw === 'string' ? { name: raw } : { id: raw?.id || raw?.resourceId, name: raw?.name };
    setActionLoading(true);
    try {
      await runItemAction((jid) => (field === 'checkedOutAt'
        ? api.checkOutResource(jid, id, ref)
        : api.checkInResource(jid, id, ref)), 'Failed to update resource');
    } catch (_) { /* surfaced */ } finally {
      setActionLoading(false);
    }
  };

  // IDI-453: Qty Used is a delta on the row (two +1s make +2).
  // IDI-816: the row is named as the server's rowID names it (id, then
  // materialId, then material_id; else position and name) -- rows made at
  // create carry only materialId. A refusal shows under that row's box, not
  // the banner; one save per row at a time. Resolves true on success.
  const handleMaterialDelta = async (idx, delta) => {
    const row = asArray(wo.materials)[idx];
    if (!row || !Number.isFinite(delta) || delta === 0) return false;
    if (materialSaving.current.has(idx)) return false;
    const jid = jobId || wo?.jobId;
    const key = row.id || row.materialId || row.material_id;
    const ref = key ? { id: key } : { index: idx, name: row.name };
    materialSaving.current.add(idx);
    setMaterialErrors((prev) => ({ ...prev, [idx]: null }));
    try {
      if (!jid) throw new Error('No parent job for this work order');
      applySavedJob(await api.adjustMaterialQtyUsed(jid, id, ref, delta));
      return true;
    } catch (err) {
      setMaterialErrors((prev) => ({ ...prev, [idx]: err?.friendlyMessage || `Failed to update materials: ${err?.message}` }));
      return false;
    } finally {
      materialSaving.current.delete(idx);
    }
  };

  // IDI-536 correction, sent as one edit_time_entry naming the entry.
  const handleCorrectEntry = async (entryId, correctedEntry) => {
    // IDI-563: `corrected` is sent and stored, so the marker survives the
    // save and a second correction keeps the first original times.
    const pick = ['clockIn', 'clockOut', 'originalClockIn', 'originalClockOut', 'corrected', 'correctedAt', 'correctedBy'];
    const entry = {};
    pick.forEach((k) => { if (correctedEntry && k in correctedEntry) entry[k] = correctedEntry[k]; });
    try {
      await runItemAction((jid) => api.editTimeEntry(jid, id, entryId, entry), 'Failed to save time correction');
    } catch (_) { /* surfaced */ }
  };

  const handleAddNote = async (content) => {
    const noteText = content || newNote.trim();
    if (!noteText) return;
    // IDI-453: one add_wo_note action. The server assigns the id and the real
    // author (never "You"); a legacy string note / instructions stay as stored.
    setNewNote('');
    try {
      await runItemAction((jid) => api.addWorkOrderNote(jid, id, noteText), 'Failed to save note');
    } catch (_) {
      setNewNote(noteText);
    }
  };

  // IDI-553: the author edits / deletes their own WO note (one rev-guarded
  // action each); the saved job replaces local notes. Rethrown so NotesPanel
  // keeps the editor open on failure (the error banner reports it).
  const handleEditNote = async (note, content) => {
    await runItemAction((jid) => api.editNote(jid, note.id, content, id), 'Failed to save note');
  };
  const handleDeleteNote = async (note) => {
    await runItemAction((jid) => api.deleteNote(jid, note.id, id), 'Failed to delete note');
  };

  // IDI-453 (Adrian 10-20): a paid seat removes any file; a field tech only a
  // file they added (author recorded on the ref); no author → paid seats only.
  // The server enforces the same rule.
  const canRemoveFile = (att) => {
    if (!att || !att.attachmentId) return false;
    if (!isFieldTech) return true;
    const author = att.authorUserId || att.author_user_id;
    return !!author && !!user?.id && author === user.id;
  };
  const deleteFileNow = async (jid, attachmentId) => {
    await api.deleteAttachment({ attachmentId, parentOwnerId: jid });
    setWO((prev) => ({
      ...prev,
      attachments: asArray(prev?.attachments).filter((a) => a?.attachmentId !== attachmentId),
    }));
  };
  // IDI-563: Remove confirms first. In view the file is deleted at once; in
  // Edit it is hidden and held until Save (Cancel restores it).
  const handleRemoveFile = async (att) => {
    const jid = jobId || wo?.jobId;
    if (!jid || !att?.attachmentId) return;
    const name = entryDisplay(att)?.name || 'this file';
    const ok = await confirm({
      title: 'Remove file?',
      message: editMode
        ? `${name} will be removed when you save. Cancel keeps it.`
        : `${name} will be removed from this work order.`,
      confirmLabel: 'Remove',
      cancelLabel: 'Keep',
      tone: 'danger',
    });
    if (!ok) return;
    if (editMode) {
      setPendingFileRemovals((prev) => (prev.includes(att.attachmentId) ? prev : [...prev, att.attachmentId]));
      return;
    }
    try {
      await deleteFileNow(jid, att.attachmentId);
    } catch (err) {
      setError(err?.friendlyMessage || `Failed to remove file: ${err.message}`);
    }
  };
  // Deletes the files removed during Edit. A failed delete keeps that file
  // (it shows again) and surfaces the error; the WO save itself stands.
  async function flushPendingFileRemovals() {
    const ids = pendingFileRemovals;
    setPendingFileRemovals([]);
    const jid = jobId || wo?.jobId;
    if (!jid || ids.length === 0) return;
    for (const attachmentId of ids) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await deleteFileNow(jid, attachmentId);
      } catch (err) {
        setError(err?.friendlyMessage || `Failed to remove file: ${err.message}`);
      }
    }
  }

  if (loading) return <div className="space-y-4 p-6"><SkeletonCard /><SkeletonCard /></div>;

  if (!wo) {
    return (
      <div className="px-6">
        <Alert variant="error">Work order not found</Alert>
        <Button variant="ghost" className="mt-4" onClick={() => navigate(-1)}>
          <ArrowLeftIcon className="h-4 w-4 mr-2" /> Back
        </Button>
      </div>
    );
  }

  // PT-28-remediation: Array.isArray guards — defensive against backend
  // shape drift where an expected array is null / object / string. Prevents
  // the `(x || []).map` crash class from leaking through if a later refactor
  // reads these into more places.
  const timeEntries = Array.isArray(wo.timeEntries)
    ? wo.timeEntries
    : Array.isArray(wo.time_entries)
    ? wo.time_entries
    : [];
  const materials = Array.isArray(wo.materials) ? wo.materials : [];
  const crew = wo.crew || wo.crewName || 'Unassigned';
  const resources = Array.isArray(wo.resources) ? wo.resources : [];

  // PT-21 (W3): Option lists for inline edit-mode dropdowns.
  // Wrapped in try/getOptions safe-noop for tests / no-provider environments.
  // PT-28 (28.2): Normalized to SelectMenu's {value,label} shape.
  const normalizeOpt = (opt) => {
    if (opt == null) return null;
    if (typeof opt === 'string' || typeof opt === 'number') {
      return { value: String(opt), label: String(opt) };
    }
    const value = opt.value ?? opt.name ?? String(opt);
    const label = opt.label ?? opt.name ?? String(value);
    return { value: String(value), label: String(label) };
  };
  // PT-22 W3-fp1 F2 / FP2-F3: crewOptions is used both in edit mode (TypeaheadChips
  // pill via crewChipOptions below) AND in display mode (resolveCrew name→ID
  // self-heal on the render surface).
  // Compute unconditionally so the display path can match legacy `wo.crew`
  // strings against the Settings directory the same way techs and resources do.
  // IDI-472: crews are keyed by their id everywhere. `crewOptions` is the raw
  // Settings list (resolveCrew reads it); `crewChipOptions` is the picker.
  const crewOptions = crewList;
  const crewChipOptions = crewPickerOptions(crewList);
  const woTypeOptions = (() => {
    if (!editMode) return [];
    // IDI-517: visible types only; the WO's current type (hidden or gone from
    // the list) is added below so it stays selected.
    const opts = asArray(getOptions?.('work_order_types')).map(normalizeOpt).filter(Boolean);
    // IDI-440: a type the dealer no longer has still shows its stored label.
    const stored = woTypeValue(wo);
    if (stored && !opts.some((o) => o.value === stored)) {
      opts.unshift({ value: stored, label: woTypeLabel(wo, getEnumLabel) });
    }
    return opts;
  })();

  // IDI-315 §3 (2026-09-12): names for the Time Tracking card's "Assigned"
  // line — mirrors the Assign card's own tech-mode / crew-mode / legacy
  // branching (:1346-1429 below) so the two cards never disagree about who's
  // on this WO. Display-only; does not affect handleClockIn/handleClockOut
  // or isClockedIn's single-flag semantics — see the CardBody comment at the
  // render site for why that matters.
  const assignedTechNames = (() => {
    const names = [];
    if (wo.assignMode === 'tech') {
      for (const t of asArray(wo.techs)) {
        const name = typeof t === 'string' ? t : (t?.name || '');
        if (name) names.push(name);
      }
    } else if (wo.assignMode === 'crew') {
      const resolvedCrew = resolveCrew(wo.crew, wo.crewId, crewOptions);
      if (resolvedCrew.crew) names.push(resolvedCrew.crew);
    } else {
      // Legacy path (assignMode absent) — same union the Assign card renders.
      if (wo.crew) {
        const resolvedCrew = resolveCrew(wo.crew, wo.crewId, crewOptions);
        if (resolvedCrew.crew) names.push(resolvedCrew.crew);
      }
      for (const m of asArray(wo.crewMembers)) {
        const name = typeof m === 'string' ? m : (m?.name || '');
        if (name) names.push(name);
      }
      for (const t of asArray(wo.techs)) {
        const name = typeof t === 'string' ? t : (t?.name || '');
        if (name) names.push(name);
      }
    }
    return names;
  })();

  // PT-21 (W3): Small helpers for editDraft mutations — keep the JSX below
  // readable by centralizing shallow set / list-mutation logic.
  const setDraft = (patch) => setEditDraft((d) => ({ ...(d || {}), ...patch }));
  const setDraftListItem = (key, idx, patch) =>
    setEditDraft((d) => {
      if (!d) return d;
      const list = Array.isArray(d[key]) ? [...d[key]] : [];
      list[idx] = { ...(list[idx] || {}), ...patch };
      return { ...d, [key]: list };
    });
  const removeDraftListItem = (key, idx) =>
    setEditDraft((d) => {
      if (!d) return d;
      const list = Array.isArray(d[key]) ? [...d[key]] : [];
      list.splice(idx, 1);
      return { ...d, [key]: list };
    });
  const appendDraftListItem = (key, item) =>
    setEditDraft((d) => {
      if (!d) return d;
      const list = Array.isArray(d[key]) ? [...d[key]] : [];
      return { ...d, [key]: [...list, item] };
    });

  return (
    <div className="px-6 max-w-5xl mx-auto">
      {/* PT-21 (21.1.1/21.1.2): DetailPageHeader + HeaderActionCluster.
          Back-affordance reads "Back to {job#} · {customer}" and links to the
          parent job. Cluster = Download (deferred/disabled per E3) · Edit
          (opens inline edit-mode, WO-2 State B) · Complete Work Order (red
          primary, guarded by handleCompleteWithGuard) · overflow kebab with
          Delete work order (IDI-469). Field techs (isFieldTech) see ONLY Download.
          In edit mode: cluster swaps to Cancel · Save Changes and status pill
          reads "Editing" (amber tone via StatusPill's editing pseudo-status). */}
      {(() => {
        const jobLabel = wo.jobTitle || wo.job_title || wo.jobNumber || wo.job_number || '';
        const custName = wo.customerName || wo.customer_name || '';
        const subtitleParts = [jobLabel, custName].filter(Boolean);
        const subtitle = subtitleParts.length ? subtitleParts.join(' • ') : undefined;
        const backTargetJobId = wo.jobId || wo.job_id || jobId;
        const backHref = backTargetJobId ? `/jobs/${backTargetJobId}` : '/jobs';
        const backLabelParts = [jobLabel, custName].filter(Boolean);
        const backLabel = backLabelParts.length
          ? `Back to ${backLabelParts.join(' · ')}`
          : 'Back to Jobs';
        // IDI-470: typed title, else type, else number; never the raw id.
        const headerTitle = (wo.title && wo.title.trim()) || woTypeLabel(wo, getEnumLabel) || woDisplayNumber(wo) || 'Work order';
        const headerStatus = editMode
          ? 'editing'
          : (normalizeWoStatus(wo.status) || '').toLowerCase();

        let actions;
        if (editMode) {
          actions = (
            <>
              <Button
                variant="ghost"
                onClick={handleCancelEdit}
                disabled={savingEdit}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSaveEdit}
                loading={savingEdit}
              >
                Save Changes
              </Button>
            </>
          );
        } else if (isFieldTech) {
          actions = (
            <Button
              variant="secondary"
              disabled
              title="WO PDF download coming soon"
            >
              <ArrowDownTrayIcon className="h-4 w-4 mr-1.5" />
              Download
            </Button>
          );
        } else {
          actions = (
            <>
              <Button
                variant="secondary"
                disabled
                title="WO PDF download coming soon"
              >
                <ArrowDownTrayIcon className="h-4 w-4 mr-1.5" />
                Download
              </Button>
              <Button
                variant="secondary"
                onClick={enterEditMode}
              >
                <PencilIcon className="h-4 w-4 mr-1.5" />
                Edit
              </Button>
              {canDelete && (
                <DropdownMenu
                  ariaLabel="More actions"
                  items={[
                    {
                      label: 'Delete work order',
                      icon: <TrashIcon className="h-4 w-4" />,
                      onClick: handleDeleteWO,
                      variant: 'danger',
                    },
                  ]}
                />
              )}
            </>
          );
        }

        return (
          <DetailPageHeader
            title={headerTitle}
            status={headerStatus || undefined}
            backHref={backHref}
            backLabel={backLabel}
            subtitle={editMode ? undefined : subtitle}
            actions={actions}
          />
        );
      })()}

      {/* IDI-467: how it was completed, and a running-clock warning (U-12). */}
      {!editMode && (completionLabel || openClockWarning) && (
        <div className="px-6 space-y-2" data-testid="wo-completion">
          {completionLabel && (
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{completionLabel}</p>
          )}
          {openClockWarning && <Alert variant="warning">{openClockWarning}</Alert>}
        </div>
      )}

      {/* PT-28 (28.6 / PT21-NOTE-1): Reusable customer card at the top of WO
          Detail (WO-2 §149). Renders the full field set (customer, primary
          contact + role, phone, email, service address, billing address,
          terms) sourced from the parent job's customer record when available,
          falling back to WO-embedded fields. Hidden in edit mode per wire §B
          ("customer card not repeated in State B"). Read-only, no status chip
          (PT-27 27.2a removed the chip from EntityDetailCard). */}
      {/* PT-39b (2026-07-27): retired EntityDetailCard in favour of the
          canonical GoldenCustomerCard atom with `surface="workorder"`. WO
          Detail is read-only (WO-2 §149), so no `onEdit` — the atom stays
          in default chevron mode and overflow sheets (contacts / addresses /
          salespeople) still open on click for VIEW.

          Data model: the raw `customer` state is loaded from the parent job's
          `customer_id`. Fallbacks preserve pre-load rendering (WO-embedded
          fields, parentJob.customer nested field). The one deliberate
          OVERRIDE is `address`: on WO Detail the physical address shown must
          be the SERVICE ADDRESS (where the work happens) — that lives on
          the parent job, not the customer's default. */}
      {/* IDI-186 (paired, closes IDI-188): view-mode header strip.
          Renders Type · Job · Customer Type · Start · Stop, replacing the
          retired right-rail info panel AND the Schedule card's view-mode
          Scheduled cell. Edit-mode: hidden (Schedule card is the sole
          date/time editor; new "Work Order Details" card owns the WO Type
          dropdown). Wire §B precedent: this same view-mode gate is used
          by GoldenCustomerCard immediately below. */}
      {!editMode && (
        <WorkOrderHeaderStrip
          wo={wo}
          customer={customer}
          settingsCtx={settingsCtx}
          getEnumLabel={getEnumLabel}
          humanizeEnum={humanizeEnum}
        />
      )}

      {!editMode && (
        <GoldenCustomerCard
          surface="workorder"
          customer={{
            ...customer,
            name: customer?.name || wo.customerName || wo.customer_name || wo?.parentJob?.customer?.name || '—',
            company: customer?.company || wo.customerCompany || wo.customer_company || wo?.parentJob?.customer?.company,
            phone: customer?.phone || wo.customerPhone || wo.customer_phone || wo?.parentJob?.customer?.phone,
            email: customer?.email || wo.customerEmail || wo.customer_email || wo?.parentJob?.customer?.email,
            // Service-address override: WO shows where the work happens,
            // sourced from the parent job. Full addresses[] stays intact for
            // the overflow sheet.
            //
            // PR-73 Commit 4 (dispatch 437 §6 step 3): the inline chain that
            // used to live here was six alternatives long, four of which had
            // zero writers on real records (dispatch 438 §1):
            //
            //   ✗ wo?.parentJob?.service_address    no writer in FE/BE/migration
            //   ✗ wo?.parentJob?.serviceAddress     no writer
            //   ✗ wo.address                        no writer on WO records
            //   ✗ wo.customer_address               no writer
            //   ✓ customer?.service_address         live (7 dev-table records
            //                                       measured by unnest-address-
            //                                       objects.py); preserved via
            //                                       helper step 8 — which now
            //                                       reads BOTH camel and snake
            //                                       per dispatch 439 §1.3.
            //   ✓ customer?.serviceAddress          BE synthesises (customers/main.go:317)
            //   ✓ customer?.address                 legacy singular
            //
            // Deleting the dead branches is not a behaviour change (all four
            // returned undefined on every render). Routing the remaining
            // three through getJobSiteAddress unifies this override with the
            // "Job site" body slot: one screen, one address resolution
            // (C-03's actual promise). The helper also adds the EVIDENCED
            // shape — job.{address1,city,state,postal} — that neither this
            // override nor the old body slot read, and which every migrated
            // job carries (dispatch 438 §1: 500/500 tenant-270 jobs).
            //
            // GoldenCustomerCard consumes address as {line1,city,state,zip};
            // getJobSiteAddress returns exactly that shape.
            address: getJobSiteAddress({ customer, job: wo?.parentJob }),
          }}
          className="mb-6"
        />
      )}

      {/* Error */}
      {error && (
        <Alert variant="error" className="mb-4" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* PT-21 (21.1.2): Legacy body-row Mark-Complete/Cancel deleted — both
          actions now live in the header cluster (Complete Work Order primary
          + overflow-kebab Delete work order). */}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column — Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Schedule & Location — PT-21 (W3): editable inline when editMode. */}
          <Card>
            <CardBody>
              {/* IDI-186 (paired, closes IDI-188): view-mode Scheduled cell
                  removed — the header strip above owns Start/Stop display.
                  Edit-mode remains untouched: DatePicker + two TimePickers
                  are the sole date/time editors (falsifier #5). Grid
                  collapses to 1-col in view-mode since Job site is the
                  only remaining cell. */}
              <div className={editMode ? "grid grid-cols-2 gap-4" : ""}>
                {editMode && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase mb-1">Schedule</p>
                    {/* PT-28 (28.2 / R1, 28.5): DatePicker + TimePicker atoms
                       replace native <input type="date|time"> — R1 flags any
                       native date/time control. Values remain ISO
                       (YYYY-MM-DD / HH:mm) so persistWO payload is unchanged. */}
                    {/* IDI-471: start and stop each have a date + time, so a
                        multi-day work order (Oct 7 → Oct 9) can be edited. */}
                    <div className="space-y-2">
                      <p className="text-xs text-gray-500">Start</p>
                      <div className="grid grid-cols-2 gap-2">
                        <DatePicker
                          value={editDraft?.scheduledDate || ''}
                          onChange={(val) => setDraft({ scheduledDate: val })}
                          aria-label="Start date"
                        />
                        <TimePicker
                          value={editDraft?.startTime || ''}
                          onChange={(val) => setDraft({ startTime: val })}
                          aria-label="Start time"
                        />
                      </div>
                      <p className="text-xs text-gray-500">Stop</p>
                      <div className="grid grid-cols-2 gap-2">
                        <DatePicker
                          value={editDraft?.endDate || ''}
                          onChange={(val) => setDraft({ endDate: val })}
                          aria-label="Stop date"
                        />
                        <TimePicker
                          value={editDraft?.endTime || ''}
                          onChange={(val) => setDraft({ endTime: val })}
                          aria-label="Stop time"
                        />
                      </div>
                      {editScheduleError && (
                        <p className="text-xs" role="alert" style={{ color: 'var(--color-error)' }}>{editScheduleError}</p>
                      )}
                    </div>
                  </div>
                )}
                <div>
                  {/* PR-73 Commit 3 (dispatch 433 §3): "Job site" is the
                      ONLY address on WO Details.
                      - Removed wo.location (editor at :1080 and persist
                        at :596; both existed to store a WO-scoped free-
                        text location that has now been unified with the
                        job-site concept).
                      - Removed wo.address from the fallback chain — one
                        WO-level address slot was kept before this
                        commit; per the ruling, both come out.
                      - Reads via getJobSiteAddress (utils/customerAddress).
                        Live against the customer today; PORTICA-25 tracks
                        converting to a snapshot.
                      - No edit input: the user changes the job site by
                        editing the customer, not the WO.

                      🟠 Existing wo.location values are ORPHANED in the
                      backend record (dispatch 428 §1 passthrough); they
                      remain in DynamoDB but no reader consumes them.
                      Disclosed in PR body — not cleaned up in this PR. */}
                  <p className="text-xs font-medium text-gray-500 uppercase mb-1">Job site</p>
                  <div className="flex items-center gap-2">
                    <MapPinIcon className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-900">
                      {/* PR-73 Commit 4: behavioural resolver. Do NOT re-inline
                          the getJobSiteAddress call. Commit 3 shipped
                          `job: wo?.job` here — an unwritten key (setWO hydrates
                          `wo.parentJob`, dispatch 436 §2) that silently made
                          helper steps 1–2 dead on this surface and reintroduced
                          the Family C disagreement C-03 exists to prevent. The
                          resolver names the correct writer and is behaviourally
                          tested at utils/customerAddress.test.js. See dispatch
                          436 §6.21a: the N-14 census applies to the argument
                          you pass, not only to the field you read. */}
                      {resolveWorkOrderJobSiteString({ wo, customer })}
                    </span>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* IDI-186 (paired, closes IDI-188): Work Order Details card.
              Owns Description (view-mode, conditional per operator ruling
              Q1 this turn) AND the WO Type dropdown (edit-mode, moved
              from the retired right-rail info panel). Clustering rationale:
              edit-mode WO Type sits adjacent to Schedule's date/time
              editor for one-mental-model "when + type" affordance.
              Falsifier #6: an empty wo.description in VIEW mode must
              NOT render an empty "Work Order Details" card — the
              outer <Card> is conditional on (editMode || wo.description).
              Falsifier #5: the edit-mode WO Type dropdown still saves. */}
          {(editMode || wo.description) && (
            <Card>
              <CardHeader>
                <span className="flex items-center gap-2 text-sm font-medium">
                  <WrenchScrewdriverIcon className="h-4 w-4 text-gray-500" />
                  Work Order Details
                </span>
              </CardHeader>
              <CardBody>
                <dl className="space-y-3">
                  {editMode && (
                    <div>
                      <dt className="text-xs font-medium text-gray-500 uppercase">WO Type</dt>
                      <dd className="text-sm text-gray-900 mt-0.5">
                        {/* PT-21 (W3, 21.2.1): WO Type editable via seeded dropdown.
                            PT-28 (28.2 / R1): SelectMenu replaces native <select>.
                            Moved from the retired right-rail info panel by IDI-186. Wiring
                            unchanged: setDraft({ woType }) → persistWO → save. */}
                        <SelectMenu
                          value={editDraft?.woType || ''}
                          onChange={(val) => { setDraft({ woType: val }); setEditTypeError(''); }}
                          options={woTypeOptions}
                          aria-label="Work order type"
                          placeholder="Select type…"
                          invalid={!!editTypeError}
                        />
                        {editTypeError && (
                          <p className="text-xs mt-1" role="alert" style={{ color: 'var(--color-error)' }}>{editTypeError}</p>
                        )}
                      </dd>
                    </div>
                  )}
                  {wo.description && (
                    <div>
                      <dt className="text-xs font-medium text-gray-500 uppercase">Description</dt>
                      <dd className="text-sm text-gray-700 mt-0.5">{wo.description}</dd>
                    </div>
                  )}
                </dl>
              </CardBody>
            </Card>
          )}

          {/* PT-22 W3 Amendment B1: Assign card — people only. Resources moved
              to its own card (below this Assign card) per WO-2 §159. Original
              WO-2 §159 wording was "after Line Items, before Attachments" —
              Line Items UI was retired by IDI-265 (2026-09-09) and
              Attachments moved to the right rail in the same PR, so the
              positional anchors changed while the intent (Resources = own
              card, not inside Assign) still holds.
              §6: one Assign card, differentiated inside by chip treatment. Crew
              mode = single chip w/ color swatch; tech mode = plain chips. */}
          <Card>
            <CardHeader>
              <span className="flex items-center gap-2 text-sm font-medium">
                <UserGroupIcon className="h-4 w-4 text-gray-500" />
                Assign
              </span>
            </CardHeader>
            <CardBody>
              {editMode ? (
                <div className="space-y-3">
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase mb-1">Crew</p>
                    {/* FP2-F3: crew renders as a dismissible pill w/ color swatch
                        via TypeaheadChips (one consistent chip treatment inside
                        Assign). Single-select semantics via max={1}. Keep both
                        `crew` (label snapshot) and `crewId` in sync on save. */}
                    <TypeaheadChips
                      variant="identity"
                      options={crewChipOptions}
                      value={editDraft?.crewId ? [editDraft.crewId] : []}
                      onChange={(next) => {
                        const picked = asArray(next)[asArray(next).length - 1] || '';
                        const opt = crewChipOptions.find((o) => o.value === picked);
                        // FP3-1 F3: snapshot the resolved crew colour onto
                        // the WO envelope. `opt.color` was already resolved
                        // via `resolveCrewColor` in `crewChipOptions` above,
                        // so display sites reading `wo.crewColor` (line ~1219
                        // + ~1271) stay consistent with Settings + picker
                        // without a second lookup.
                        setDraft({
                          crewId: picked,
                          crew: opt?.label || '',
                          crewColor: opt?.color || '',
                        });
                      }}
                      placeholder={
                        crewChipOptions.length === 0
                          ? 'No crews on file'
                          : 'Select crew…'
                      }
                      emptyMessage={
                        crewChipOptions.length === 0
                          ? 'No crews on file'
                          : 'No matching crews'
                      }
                      allowCreate={true}
                      max={1}
                      aria-label="Assigned crew"
                    />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase mb-1">Techs</p>
                    {/* PT-22 W3 §3.4-EDIT: TypeaheadChips replaces the legacy
                        Input+Add block. Value is resolved IDs only; unresolved
                        (legacy name-only) records ride the strip below. */}
                    <TypeaheadChips
                      options={[
                        ...asArray(techPool),
                        // IDI-681 D2: a deactivated tech kept on this WO isn't
                        // offered, but its chip reads the saved name with the
                        // Deactivated icon (chip-only; never saved).
                        ...deactivatedChipOptions(
                          asArray(editDraft?.techs).filter((t) => t.resolved),
                          asArray(techPool),
                          deactivatedTechIds
                        ),
                      ]}
                      value={asArray(editDraft?.techs).filter((t) => t.resolved).map((t) => t.id)}
                      onChange={(ids) => {
                        const idSet = new Set(asArray(ids));
                        const kept = asArray(editDraft?.techs).filter((t) => !t.resolved || idSet.has(t.id));
                        const existingIds = new Set(kept.filter((t) => t.resolved).map((t) => t.id));
                        const added = asArray(ids)
                          .filter((tid) => !existingIds.has(tid))
                          .map((tid) => {
                            const opt = asArray(techPool).find((o) => o.value === tid);
                            return { id: tid, name: opt ? opt.label : tid, resolved: true };
                          });
                        setDraft({ techs: [...kept, ...added] });
                      }}
                      placeholder="Search techs…"
                      emptyMessage={
                        asArray(techPool).length === 0
                          ? 'No field techs on file'
                          : 'No matching techs'
                      }
                      allowCreate={true}
                      onCreate={(name) => setDraft({ techs: [...asArray(editDraft?.techs), { id: null, name, resolved: false }] })}
                    />
                    {/* Unresolved-strip: legacy name-only records survive here
                        with id:null. They round-trip on save and self-heal
                        when someone picks a directory match. */}
                    {asArray(editDraft?.techs).some((t) => !t.resolved) && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {asArray(editDraft?.techs)
                          .map((t, idx) => ({ t, idx }))
                          .filter(({ t }) => !t.resolved)
                          .map(({ t, idx }) => (
                            <span
                              key={`u-${idx}`}
                              className="inline-flex items-center gap-1 rounded-full bg-gray-50 px-2 py-0.5 text-xs text-gray-400 italic border border-dashed border-[color:var(--color-border)]"
                              title="Not in directory"
                            >
                              {t.name}
                              <button
                                type="button"
                                onClick={() => removeDraftListItem('techs', idx)}
                                className="text-gray-400 hover:text-red-600"
                                aria-label={`Remove ${t.name}`}
                              >
                                ×
                              </button>
                            </span>
                          ))}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  {/* PT-22 W3 §6: crew mode = single chip w/ swatch;
                      tech mode = plain chips. Legacy records without
                      assignMode render defensively — do NOT infer mode.
                      PT-22 W3-fp1 F2: crew resolves name→ID on display too,
                      so a legacy record with `wo.crew='Alpha Dogs'` and no
                      `wo.crewId` renders as a resolved chip (with swatch)
                      here as long as it matches a Settings crew. Dashed/
                      italic appears only for genuinely-unresolvable names —
                      the true "someone has to map this" signal. */}
                  {(() => {
                    const resolvedCrew = resolveCrew(wo.crew, wo.crewId, crewOptions);
                    const inCrewMode = wo.assignMode === 'crew' && (resolvedCrew.crew || crew);
                    return inCrewMode ? (
                      <div className="flex flex-wrap gap-1">
                        {resolvedCrew.resolved ? (
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700 cursor-pointer hover:bg-gray-200 transition-colors" onClick={() => setCrewModalOpen(true)} role="button" tabIndex={0}>
                            <span
                              className="inline-block h-2 w-2 rounded-full"
                              style={{ backgroundColor: resolvedCrew.color }}
                              aria-hidden="true"
                            />
                            {resolvedCrew.crew || crew}
                          </span>
                        ) : (
                          <span
                            className="inline-flex items-center gap-1.5 rounded-full bg-gray-50 px-2 py-0.5 text-xs text-gray-400 italic border border-dashed border-[color:var(--color-border)]"
                            title="Not in directory"
                          >
                            {resolvedCrew.crew || crew}
                          </span>
                        )}
                      </div>
                    ) : null;
                  })()}
                  {wo.assignMode === 'tech' && asArray(wo.techs).length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {asArray(wo.techs).map((t, idx) => {
                        const name = typeof t === 'string' ? t : (t?.name || '');
                        const hasId = t && typeof t === 'object' && t.id;
                        return (
                          <span
                            key={t?.id || `${name}-${idx}`}
                            className={
                              hasId
                                ? 'inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700'
                                : 'inline-flex items-center rounded-full bg-gray-50 px-2 py-0.5 text-xs text-gray-400 italic border border-dashed border-[color:var(--color-border)]'
                            }
                            title={hasId ? undefined : 'Not in directory'}
                          >
                            {name}
                          </span>
                        );
                      })}
                    </div>
                  ) : null}
                  {/* Legacy path (assignMode absent): render whatever exists
                      as chips — do NOT infer mode.
                      PT-22 W3-fp1 F2: crew name still goes through resolveCrew
                      here so a legacy record with `wo.crew='Alpha Dogs'` and
                      no `wo.crewId` gets the resolved chip variant when the
                      name hits the directory. */}
                  {!wo.assignMode &&
                    (wo.crew || (Array.isArray(wo.crewMembers) && wo.crewMembers.length > 0) || asArray(wo.techs).length > 0) ? (
                    <div className="flex flex-wrap gap-1">
                      {wo.crew && (() => {
                        const resolvedCrew = resolveCrew(wo.crew, wo.crewId, crewOptions);
                        return resolvedCrew.resolved ? (
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700 cursor-pointer hover:bg-gray-200 transition-colors" onClick={() => setCrewModalOpen(true)} role="button" tabIndex={0}>
                            <span
                              className="inline-block h-2 w-2 rounded-full"
                              style={{ backgroundColor: resolvedCrew.color }}
                              aria-hidden="true"
                            />
                            {resolvedCrew.crew}
                          </span>
                        ) : (
                          <span
                            className="inline-flex items-center gap-1.5 rounded-full bg-gray-50 px-2 py-0.5 text-xs text-gray-400 italic border border-dashed border-[color:var(--color-border)]"
                            title="Not in directory"
                          >
                            {resolvedCrew.crew}
                          </span>
                        );
                      })()}
                      {asArray(wo.crewMembers).map((m, idx) => {
                        const name = typeof m === 'string' ? m : (m?.name || '');
                        return (
                          <span
                            key={`cm-${idx}`}
                            className="inline-flex items-center rounded-full bg-gray-50 px-2 py-0.5 text-xs text-gray-400 italic border border-dashed border-[color:var(--color-border)]"
                            title="Not in directory"
                          >
                            {name}
                          </span>
                        );
                      })}
                      {asArray(wo.techs).map((t, idx) => {
                        const name = typeof t === 'string' ? t : (t?.name || '');
                        const hasId = t && typeof t === 'object' && t.id;
                        return (
                          <span
                            key={`t-${idx}`}
                            className={
                              hasId
                                ? 'inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700'
                                : 'inline-flex items-center rounded-full bg-gray-50 px-2 py-0.5 text-xs text-gray-400 italic border border-dashed border-[color:var(--color-border)]'
                            }
                            title={hasId ? undefined : 'Not in directory'}
                          >
                            {name}
                          </span>
                        );
                      })}
                    </div>
                  ) : null}
                  {/* True-empty fallback */}
                  {!(wo.assignMode === 'crew' && (wo.crew || crew)) &&
                    !(wo.assignMode === 'tech' && asArray(wo.techs).length > 0) &&
                    !(!wo.assignMode &&
                      (wo.crew || (Array.isArray(wo.crewMembers) && wo.crewMembers.length > 0) || asArray(wo.techs).length > 0)) && (
                      <p className="text-xs text-gray-400">Unassigned</p>
                    )}
                </div>
              )}
            </CardBody>
          </Card>

          {/* Time Tracking */}
          <Card>
            <CardHeader action={
              (
                !isClockedIn ? (
                  /* PT-21 (21.6.2): red-outline treatment for Clock In (no
                     danger-outline variant on Button today; use ghost + explicit
                     red border/text via className). */
                  <Button
                    size="sm"
                    onClick={handleClockIn}
                    loading={actionLoading}
                    variant="ghost"
                    className="border border-[color:var(--color-error)] text-[color:var(--color-error)] hover:bg-[color:color-mix(in_srgb,var(--color-error)_8%,transparent)]"
                  >
                    <PlayIcon className="h-3.5 w-3.5 mr-1" />
                    Clock In
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={handleClockOut}
                    loading={actionLoading}
                    className="border border-[color:var(--color-error)] text-[color:var(--color-error)] hover:bg-[color:color-mix(in_srgb,var(--color-error)_8%,transparent)]"
                  >
                    <StopIcon className="h-3.5 w-3.5 mr-1" />
                    Clock Out
                  </Button>
                )
              )
            }>
              <span className="flex items-center gap-2 text-sm font-medium">
                <ClockIcon className="h-4 w-4 text-gray-500" />
                Time Tracking
              </span>
            </CardHeader>
            <CardBody>
              {/* IDI-315 §3 (2026-09-12): surface WHO is assigned, without
                  implying per-tech clock-in state that does not exist.
                  isClockedIn (:496) is a single WO-level flag — ONE open
                  time entry at a time, for whoever clicks Clock In, not
                  gated per assigned tech. An assigned tech who never clicks
                  the button had NO surface telling a viewer they were even
                  assigned to this WO's time tracking at all (the crew/tech
                  roster lives in a separate Assign card above, easy to miss
                  as the reason for an apparently-missing name here). This is
                  a display addition only — it does NOT add per-tech status
                  indicators, and does not change handleClockIn/handleClockOut
                  or isClockedIn's single-flag semantics. The sentence below
                  is deliberate, not filler: it exists so the next reader
                  doesn't mistake the single shared clock state for a bug and
                  "fix" it into implying concurrent per-tech tracking that the
                  data model (and IDI-214, tracked separately) doesn't support.
              */}
              {assignedTechNames.length > 0 && (
                <p className="text-xs mb-3" style={{ color: 'var(--color-text-muted)' }}>
                  Assigned: {assignedTechNames.join(', ')}. Clock-in is per
                  work order, not per person — whichever assignee clocks in
                  covers the whole crew's time on this WO.
                </p>
              )}
              {timeEntries.length > 0 ? (
                <div>
                  {asArray(timeEntries).map((entry, idx) => (
                    <TimeEntry
                      key={entry.id || idx}
                      entry={entry}
                      canEdit={!isFieldTech}
                      correctedBy={user?.name || user?.email || 'Unknown'}
                      // IDI-453: one edit_time_entry; the server recomputes
                      // actualHours (IDI-536) and stamps who corrected it.
                      onCorrect={handleCorrectEntry}
                    />
                  ))}
                  {/* Total */}
                  <div
                    className="flex items-center justify-between pt-3 mt-2 border-t"
                    style={{ borderColor: 'var(--color-border)' }}
                  >
                    <span className="text-sm font-medium text-gray-700">Total Time</span>
                    <span className="text-sm font-bold text-gray-900">
                      {(() => {
                        // PT-28 cold-eye 3 (S2 WO-2 §152 / WIRE-07): match the
                        // guard the server's actualHours uses — the old reduce
                        // discarded rows with MISSING clockIn/clockOut but not
                        // INVALID ones. An Invalid Date subtraction returns
                        // NaN, which propagates through the fold and renders
                        // `NaNh NaNm`. Same Number.isFinite(ms) && ms > 0
                        // predicate as the server's actualHours (which Job Costing
                        // reads for Labor, and which is already correct).
                        const totalMs = asArray(timeEntries).reduce((sum, e) => sum + entryDurationMs(e), 0);
                        const hours = Math.floor(totalMs / 3600000);
                        const minutes = Math.floor((totalMs % 3600000) / 60000);
                        return `${hours}h ${minutes}m`;
                      })()}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <ClockIcon className="h-6 w-6 text-gray-300 mx-auto mb-2" />
                  <p className="text-xs text-gray-500">
                    Time tracking starts when you clock in
                  </p>
                </div>
              )}
            </CardBody>
          </Card>

          {/* Materials */}
          <Card>
            <CardHeader>
              <span className="flex items-center gap-2 text-sm font-medium">
                <WrenchScrewdriverIcon className="h-4 w-4 text-gray-500" />
                Materials (Quoted / Used)
              </span>
            </CardHeader>
            <CardBody>
              {editMode ? (
                /* PT-21 (W3, 21.2.1): Materials editable inline — name / unit /
                    total cells write to editDraft; "Used" (live-action) is
                    hidden while editing to keep the surface unambiguous. */
                <div className="space-y-2">
                  {(editDraft?.materials || []).length === 0 && (
                    <p className="text-xs text-gray-500">No materials yet</p>
                  )}
                  {/* PT-28 (28.5): Materials inline-edit cells use DS atoms
                      (Input / NumberField) with tokenized borders — no more
                      hardcoded border-[color:var(--color-border)]. Remove button becomes an
                      IconButton so the whole row is atom-composed. */}
                  {asArray(editDraft?.materials).map((m, idx) => (
                    <div key={idx} className="grid grid-cols-[1fr_5rem_4rem_2rem] gap-2 items-center">
                      <Input
                        type="text"
                        value={m.name || ''}
                        onChange={(e) => setDraftListItem('materials', idx, { name: e.target.value })}
                        placeholder="Material name"
                        aria-label="Material name"
                      />
                      <NumberField
                        kind="decimal"
                        value={
                          m.total === '' || m.total == null || Number.isNaN(Number(m.total))
                            ? null
                            : Number(m.total)
                        }
                        onChange={(num) =>
                          setDraftListItem('materials', idx, {
                            total: num == null ? '' : num,
                          })
                        }
                        placeholder="Total"
                        aria-label="Material total quantity"
                        decimals={2}
                      />
                      <Input
                        type="text"
                        value={m.unit || ''}
                        onChange={(e) => setDraftListItem('materials', idx, { unit: e.target.value })}
                        placeholder="Unit"
                        aria-label="Material unit"
                      />
                      <IconButton
                        onClick={() => removeDraftListItem('materials', idx)}
                        aria-label="Remove material"
                      >
                        <XCircleIcon className="h-4 w-4" />
                      </IconButton>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() =>
                      appendDraftListItem('materials', { name: '', total: '', unit: 'sq ft', used: 0 })
                    }
                    className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
                  >
                    <PlusIcon className="h-3 w-3" /> Add material
                  </button>
                </div>
              ) : (
                <MaterialsSection
                  wo={wo}
                  materials={materials}
                  onUpdateMaterial={handleMaterialDelta}
                  rowErrors={materialErrors}
                />
              )}
            </CardBody>
          </Card>

          {/* IDI-265 (2026-09-09): Line Items UI section removed — the section
              rendered `wo.lineItems` (the object array) but no cohort writes
              that field: migrated WOs get neither `materials` nor `lineItems`
              (migration/cmd/migrate/entities.go:837-869); pre-B5 app-created
              WOs wrote only `lineItemIds`, never the objects; post-B5 WOs seed
              `materials` instead. The section was therefore empty on every
              real record. Materials supersedes.
              ⚠️ LOAD-BEARING: `wo.lineItems` and `wo.lineItemIds` MUST still
              be seeded into editDraft (:550-551) and persisted through
              persistWO (:603-604). They are the WO↔line-item join added by
              IDI-82. Do not remove either writer. */}

          {/* PT-22 W3 Amendment B2–B3: Resources card — own card, after Line
              Items / Materials, before Attachments per WO-2 §159. Custody ledger
              shape (Item / Status / action) per WO-2 §80–83. No inventory logic
              invented (WS-C out of scope per WO-2 §154). Actor attribution not
              yet threaded through `handleResourceCheck` — TD. */}
          <Card>
            <CardHeader>
              <span className="flex items-center gap-2 text-sm font-medium">
                <WrenchScrewdriverIcon className="h-4 w-4 text-gray-500" />
                Resources
              </span>
            </CardHeader>
            <CardBody>
              {editMode ? (
                <div>
                  {/* PT-22 W3 §3.4-EDIT: TypeaheadChips over Settings resource
                      list. Same resolve-on-edit contract as techs above. */}
                  <TypeaheadChips
                    options={resourcePool}
                    value={asArray(editDraft?.resources).filter((r) => r.resolved).map((r) => r.id)}
                    onChange={(ids) => {
                      const idSet = new Set(asArray(ids));
                      const kept = asArray(editDraft?.resources).filter((r) => !r.resolved || idSet.has(r.id));
                      const existingIds = new Set(kept.filter((r) => r.resolved).map((r) => r.id));
                      const added = asArray(ids)
                        .filter((rid) => !existingIds.has(rid))
                        .map((rid) => {
                          const opt = asArray(resourcePool).find((o) => o.value === rid);
                          return {
                            id: rid,
                            name: opt ? opt.label : rid,
                            resolved: true,
                            category: opt?.category,
                          };
                        });
                      setDraft({ resources: [...kept, ...added] });
                    }}
                    placeholder="Search resources…"
                    emptyMessage={
                      asArray(resourcePool).length === 0
                        ? 'No resources on file'
                        : 'No matching resources'
                    }
                    allowCreate={true}
                  />
                  {asArray(editDraft?.resources).some((r) => !r.resolved) && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {asArray(editDraft?.resources)
                        .map((r, idx) => ({ r, idx }))
                        .filter(({ r }) => !r.resolved)
                        .map(({ r, idx }) => (
                          <span
                            key={`ur-${idx}`}
                            className="inline-flex items-center gap-1 rounded-full bg-gray-50 px-2 py-0.5 text-xs text-gray-400 italic border border-dashed border-[color:var(--color-border)]"
                            title="Not in directory"
                          >
                            {r.name}
                            <button
                              type="button"
                              onClick={() => removeDraftListItem('resources', idx)}
                              className="text-gray-400 hover:text-red-600"
                              aria-label={`Remove ${r.name}`}
                            >
                              ×
                            </button>
                          </span>
                        ))}
                    </div>
                  )}
                </div>
              ) : asArray(resources).length > 0 ? (
                /* Custody ledger — WO-2 §80–83. Item / Status / Action. */
                <div className="space-y-1">
                  {asArray(resources).map((res, idx) => {
                    const rObj = typeof res === 'string' ? { name: res } : res;
                    const inAt = rObj.checkedInAt;
                    const outAt = rObj.checkedOutAt;
                    // FP2-F6: title-case seed categories via getEnumLabel.
                    const catLabel = rObj.category
                      ? (getEnumLabel?.('resource_category', rObj.category) || rObj.category)
                      : '';
                    const label = catLabel
                      ? `${rObj.name || String(res)} · ${catLabel}`
                      : (rObj.name || String(res));
                    // Status semantics (WO-1 §126): assignment !== check-out.
                    let statusEl;
                    if (outAt && inAt) {
                      // IDI-563 (L-08): "Checked in by <name>, <time>".
                      const t = new Date(inAt);
                      const timeStr = Number.isFinite(t.getTime())
                        ? t.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
                        : '';
                      const inBy = rObj.checkedInByName;
                      statusEl = (
                        <span className="text-xs text-green-600">
                          {inBy
                            ? `Checked in by ${inBy}${timeStr ? `, ${timeStr}` : ''}`
                            : `Checked in${timeStr ? ` · ${timeStr}` : ''}`}
                        </span>
                      );
                    } else if (outAt) {
                      // IDI-453 (L-08): "Checked out by <name>, <time>".
                      const t = new Date(outAt);
                      const timeStr = Number.isFinite(t.getTime())
                        ? t.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
                        : '';
                      const by = rObj.checkedOutByName;
                      statusEl = (
                        <span className="text-xs text-gray-600">
                          {by
                            ? `Checked out by ${by}${timeStr ? `, ${timeStr}` : ''}`
                            : `Checked out${timeStr ? ` · ${timeStr}` : ''}`}
                        </span>
                      );
                    } else {
                      statusEl = <span className="text-xs text-gray-400">Available</span>;
                    }
                    return (
                      <div key={idx} className="flex items-center gap-3 py-1">
                        <span className="text-sm text-gray-700 flex-1">{label}</span>
                        <span className="min-w-[8rem] text-right">{statusEl}</span>
                        {!outAt ? (
                          <Button
                            size="sm"
                            variant="secondary"
                            disabled={actionLoading}
                            onClick={() => handleResourceCheck(idx, 'checkedOutAt')}
                          >
                            Check out
                          </Button>
                        ) : !inAt ? (
                          <Button
                            size="sm"
                            variant="secondary"
                            disabled={actionLoading}
                            onClick={() => handleResourceCheck(idx, 'checkedInAt')}
                          >
                            Check in
                          </Button>
                        ) : (
                          <span className="w-[5.5rem]" aria-hidden="true" />
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-xs text-gray-400">None assigned</p>
              )}
            </CardBody>
          </Card>

        </div>

          {/* GP-10/5a: Crew read-only modal — click chip to view, Edit to reassign */}
          <Dialog open={crewModalOpen} onClose={() => setCrewModalOpen(false)}>
            <DialogHeader>Assigned Crew</DialogHeader>
            <DialogBody>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  {(() => {
                    const ident = crewIdentity(wo, crewList);
                    return (
                      <>
                        <span className="inline-block h-3 w-3 rounded-full" style={{ backgroundColor: ident.assigned ? ident.color : 'var(--neutral-500)' }} />
                        <span className="text-sm font-medium">{ident.name || 'Unassigned'}</span>
                      </>
                    );
                  })()}
                </div>
                {asArray(wo?.techs).length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase mb-1">Techs</p>
                    <div className="flex flex-wrap gap-1">
                      {asArray(wo?.techs).map((t, i) => (
                        <span key={i} className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
                          {typeof t === 'string' ? t : t?.name || ''}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex justify-end pt-3 border-t border-gray-200">
                  <Button variant="secondary" size="sm" onClick={() => { setCrewModalOpen(false); setEditMode(true); }}>
                    Edit Assignment
                  </Button>
                </div>
              </div>
            </DialogBody>
          </Dialog>

        {/* Right Column — Notes & Activity */}
        <div className="space-y-6">
          {/* IDI-186 (paired, closes IDI-188): right-rail info panel retired.
              Its five children are now homed as follows:
                · WO Type view-mode    → WorkOrderHeaderStrip (view-mode)
                · WO Type edit-mode    → Work Order Details card (edit-mode)
                · Job                  → WorkOrderHeaderStrip (view-mode)
                · CustomerTypeCell     → WorkOrderHeaderStrip (view-mode)
                · Description          → Work Order Details card (view-mode,
                                          conditional per operator Q1 ruling)
              Falsifier #1 (BUILD SCOPE v3): no rendered instance of the
              retired panel survives — behavioral test in
              WorkOrderHeaderStrip.idi186.test.js enforces the assertion
              via DOM shape rather than source grep (behavioral over regex
              per the pattern banked on IDI-176). Falsifier #6: an empty description renders
              NO empty "Work Order Details" card (its outer conditional is
              `editMode || wo.description`). */}

          {/* Notes */}
          {/* IDI-553: the author (any role, field techs included) edits or
              deletes their own note; derived string / instructions entries
              stay read-only. An edited note sorts to the top. */}
          <NotesPanel
            notes={sortWoNotesNewestFirst(asArray(notes).map(n => ({
              id: n.id,
              content: n.content || n.text,
              author: n.author,
              author_id: n.author_id || n.authorId,
              created_at: n.created_at || n.createdAt,
              updated_at: n.updated_at || n.updatedAt,
              readOnly: !!n.readOnly,
            })))}
            onAdd={editMode ? undefined : handleAddNote}
            onEdit={editMode ? undefined : handleEditNote}
            onDelete={editMode ? undefined : handleDeleteNote}
            currentUserId={user?.id || null}
            canModify={(n) => !n.readOnly && !!n.id}
          />

          {/* Attachments — PT-21 (W3, 21.2.1): edit-mode chip list with remove;
              "+ Add" is disabled per E4 sign-off (WO document upload endpoint
              missing). Do NOT introduce local File state. */}
          <Card>
            <CardHeader>
              <span className="flex items-center gap-2 text-sm font-medium">
                Attachments
              </span>
            </CardHeader>
            <CardBody>
              {editMode ? (
                <div className="flex flex-wrap gap-2">
                  {/* IDI-453: the live list (a file attached here shows at
                    * once). IDI-563: a removal is held until Save. */}
                  {asArray(wo.attachments)
                    .filter((att) => !(att?.attachmentId && pendingFileRemovals.includes(att.attachmentId)))
                    .map((att, idx) => {
                    const v = entryDisplay(att);
                    return (
                      <span
                        key={`${v.key || att.attachmentId || att.id || 'file'}-${idx}`}
                        className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700"
                      >
                        {v.name || `File ${idx + 1}`}
                        {canRemoveFile(att) && (
                          <button
                            type="button"
                            onClick={() => handleRemoveFile(att)}
                            className="text-gray-400 hover:text-red-600"
                            aria-label="Remove attachment"
                          >
                            ×
                          </button>
                        )}
                      </span>
                    );
                  })}
                  {/* PT-53b commit 1: flag-gated + Add. When the flag is off, the
                    * button stays disabled with its honest title copy. When the
                    * flag flips (S3_BUCKET_FILES lands on the deployed Lambda),
                    * the button becomes an enabled CTA that opens AttachModal.
                    * Same element; only the disabled/onClick/title props change.
                    */}
                  {isEnabled('ATTACHMENTS_UPLOAD') ? (
                    <button
                      type="button"
                      onClick={() => setAttachOpen(true)}
                      className="inline-flex items-center gap-1 rounded-full border border-dashed px-2 py-0.5 text-xs text-gray-600 hover:text-gray-900"
                      style={{ borderColor: 'var(--color-border)' }}
                    >
                      <PlusIcon className="h-3 w-3" /> Add
                    </button>
                  ) : (
                    // PR-64 §8 (378_ §3): a disabled control that looks identical
                    // to a broken one IS a defect. The hover-only `title` isn't
                    // enough on a discovery-facing surface; pair it with visible
                    // helper text so the user learns WHY without hovering. Same
                    // disabled state; new visible copy. Removes when the backend
                    // upload endpoint (PORTICA-55) lands and ATTACHMENTS_UPLOAD flips on.
                    <>
                      <button
                        type="button"
                        disabled
                        title="Attachment uploads are pending a backend endpoint. Coming soon."
                        className="inline-flex items-center gap-1 rounded-full border border-dashed px-2 py-0.5 text-xs text-gray-400 cursor-not-allowed"
                        style={{ borderColor: 'var(--color-border)' }}
                      >
                        <PlusIcon className="h-3 w-3" /> Add
                      </button>
                      <p className="basis-full text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                        Attachment uploads are pending a backend endpoint. Coming soon.
                      </p>
                    </>
                  )}
                </div>
              ) : (!wo.attachments || wo.attachments.length === 0) ? (
                <div>
                  <p className="text-xs text-gray-500 text-center py-2">No attachments</p>
                  {isEnabled('ATTACHMENTS_UPLOAD') && (
                    <div className="flex justify-center">
                      <button
                        type="button"
                        onClick={() => setAttachOpen(true)}
                        className="inline-flex items-center gap-1 rounded-full border border-dashed px-2 py-0.5 text-xs text-gray-600 hover:text-gray-900"
                        style={{ borderColor: 'var(--color-border)' }}
                      >
                        <PlusIcon className="h-3 w-3" /> Add
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  {/* PT-53b commit 1: entries are a union of legacy inline
                    * documents and new AttachmentRefs. `entryDisplay` normalises
                    * both into a common view shape; branching is deliberate +
                    * testable in utils/attachmentEntry.js, not inferred here. */}
                  {asArray(wo.attachments).map((att, idx) => {
                    const v = entryDisplay(att);
                    return (
                      <div key={`${v.key || att.attachmentId || att.id || 'file'}-${idx}`} className="flex items-center gap-2 text-sm">
                        {/* IDI-445: the name opens the shared preview modal. */}
                        <button
                          type="button"
                          onClick={() => setPreviewAtt(att)}
                          className="text-gray-900 hover:underline text-left break-all"
                        >
                          {v.name || `File ${idx + 1}`}
                        </button>
                        {/* R-PROV: library-sourced files display source label. */}
                        {v.source === 'library' && (
                          <span className="text-xs text-gray-500">· Library</span>
                        )}
                        {canRemoveFile(att) && (
                          <button
                            type="button"
                            onClick={() => handleRemoveFile(att)}
                            className="text-xs text-gray-400 hover:text-red-600"
                            aria-label={`Remove ${v.name || 'file'}`}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    );
                  })}
                  {isEnabled('ATTACHMENTS_UPLOAD') && (
                    <button
                      type="button"
                      onClick={() => setAttachOpen(true)}
                      className="mt-2 inline-flex items-center gap-1 rounded-full border border-dashed px-2 py-0.5 text-xs text-gray-600 hover:text-gray-900"
                      style={{ borderColor: 'var(--color-border)' }}
                    >
                      <PlusIcon className="h-3 w-3" /> Add
                    </button>
                  )}
                </div>
              )}
            </CardBody>
          </Card>

          {/* PT-53b commit 1: cross-scope attach modal. Owner-carries-references
            * per FILES_SCOPE_AND_VISIBILITY.md §4b — the modal calls onAttached
            * with an AttachmentRef, and handleAttached appends to wo.attachments[]
            * via persistWO. Rendered unconditionally (the modal is closed when
            * attachOpen=false and the CTA is flag-gated), so the JSX shape is
            * stable across flag flips. */}
          <AttachModal
            /* IDI-55: "Choose from library" is on only here and on quote Review & Send + WO Attachments (Adrian 10-21). */
            allowLibrary
            open={attachOpen}
            onClose={() => setAttachOpen(false)}
            scope="workOrder"
            ownerId={id}
            // IDI-315 §1 (2026-09-12): was `wo?.title || formatWorkOrderNumber(wo)`.
            // wo.title is NOT a generic WO identifier — WOCreateSheets.js's
            // suggestTitleFromItems() sets it from the FIRST selected line
            // item's area/scope/item_name at creation time (e.g. "Ceilings —
            // Blown-in cellulose"), so the Attach modal was naming a product,
            // not the work order, whenever that title-suggestion fired. Fixed
            // to always resolve to `{WO number} · {WO type}` regardless of
            // what wo.title contains — the WO number always identifies the
            // work order; the type (via getEnumLabel, same accessor used for
            // the edit-mode WO Type SelectMenu above) adds context without
            // ever risking a product name leaking into the label.
            ownerLabel={[
              woDisplayNumber(wo) || null,
              woTypeLabel(wo, getEnumLabel) || null,
            ].filter(Boolean).join(' · ') || 'Work order'}
            companyId={user?.tenantId}
            parentOwnerId={jobId || wo?.jobId}
            onAttached={handleAttached}
          />

          {/* IDI-445 D2: Delete per canRemoveFile, through the card's own
            * Remove confirm (handleRemoveFile). */}
          {previewAtt && (
            <FilePreviewModal
              entry={previewAtt}
              onClose={() => setPreviewAtt(null)}
              onDelete={canRemoveFile(previewAtt)
                ? () => { const a = previewAtt; setPreviewAtt(null); return handleRemoveFile(a); }
                : undefined}
            />
          )}

          {/* IDI-265 (2026-09-09): supersedes the earlier PT-21 (21.4.1)
              placement. The Line Items UI section has been removed entirely
              (materials supersedes; see the comment where the section used to
              render). The Attachments card is now here in the right rail,
              beneath NotesPanel. */}
        </div>
      </div>
    </div>
  );
}
